import { useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useModal } from '@/lib/hooks/useModal';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { getAdminHomeRoute, isAdminRole, isCmsAdminRole, isFullAdminRole, ROUTES, SHOP_URL } from '@/lib/constants';
import { useFeatureFlag } from '@/lib/hooks/useFeatureFlag';
import { quizzesApi } from '@/api/endpoints/quizzes';
import { queryKeys } from '@/lib/queryKeys';

const SITE_NAV_LINKS = [
  { label: 'About', path: '/about' },
  { label: 'How it Works', path: '/#how-it-works' },
  { label: 'Products', path: '/products' },
  { label: 'Technology', path: '/technology' },
  { label: 'Blog', path: '/blog' },
  { label: 'Events', path: '/events' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact Us', path: '/contact' },
];

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors ${isActive ? 'text-ea-blue' : 'text-ea-black hover:text-ea-blue'}`
      }
    >
      {children}
    </NavLink>
  );
}

function ExternalNavItem({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="text-sm font-medium text-ea-black hover:text-ea-blue"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

export function Header() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = isAdminRole(user?.roleId);
  const isFullAdmin = isFullAdminRole(user?.roleId);
  const isCmsAdmin = isCmsAdminRole(user?.roleId);
  const { enabled: algorithmMgmtEnabled } = useFeatureFlag('new-pattern-solver');
  const { enabled: quizEngineV2Enabled } = useFeatureFlag('quiz-engine-v2');
  const { enabled: sesEmailEngineEnabled } = useFeatureFlag('ses-email-engine');

  const { data: pendingApprovals } = useQuery({
    queryKey: queryKeys.axisMatrix.pendingApprovals(),
    queryFn: async () => {
      const { data } = await quizzesApi.getPendingApprovals();
      return data;
    },
    enabled: !!user && !isCmsAdmin,
  });
  const pendingCount = pendingApprovals?.count ?? 0;
  const mobileMenuOpen = useModal();
  const userMenuOpen = useModal();
  const siteMenuOpen = useModal();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const siteMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    userMenuOpen.close();
    logout();
    navigate(ROUTES.LOGIN);
  };

  const closeUserMenu = userMenuOpen.close;
  useEffect(() => {
    if (!userMenuOpen.isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        closeUserMenu();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen.isOpen, closeUserMenu]);

  // Close site-menu dropdown when clicking outside (desktop only — mobile uses the off-canvas overlay)
  const closeSiteMenuEffect = siteMenuOpen.close;
  useEffect(() => {
    if (!siteMenuOpen.isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (siteMenuRef.current && !siteMenuRef.current.contains(e.target as Node)) {
        closeSiteMenuEffect();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [siteMenuOpen.isOpen, closeSiteMenuEffect]);

  const homeLink = user ? (isAdmin ? getAdminHomeRoute(user.roleId) : ROUTES.DASHBOARD) : ROUTES.LANDING;

  function closeMobile() {
    mobileMenuOpen.close();
  }

  function closeSiteMenu() {
    siteMenuOpen.close();
  }

  function handleHamburgerClick() {
    // On mobile, toggle the full off-canvas panel; on desktop, toggle the dropdown
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      mobileMenuOpen.toggle();
      siteMenuOpen.close();
    } else {
      siteMenuOpen.toggle();
      mobileMenuOpen.close();
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b border-ea-07 bg-white px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-8">
          {/* Hamburger button — visible on all screen sizes */}
          <div className="relative" ref={siteMenuRef}>
            <button
              className="flex items-center gap-2 text-ea-02 hover:text-ea-blue transition-colors"
              onClick={handleHamburgerClick}
              aria-label="Site menu"
              aria-expanded={siteMenuOpen.isOpen || mobileMenuOpen.isOpen}
              aria-haspopup="true"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="hidden text-sm font-medium md:inline">Menu</span>
            </button>

            {/* Desktop dropdown panel */}
            {siteMenuOpen.isOpen && (
              <div className="absolute left-0 top-full z-60 mt-3 hidden w-56 rounded-lg border border-ea-07 bg-white py-2 shadow-lg md:block">
                {SITE_NAV_LINKS.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    onClick={closeSiteMenu}
                    className="block px-4 py-2.5 text-sm font-medium text-ea-black transition-colors hover:bg-ea-11 hover:text-ea-blue"
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink to={homeLink}>
            <img src="/img/EndoAxis-logo.svg" alt="EndoAxis" className="h-8" />
          </NavLink>
          {user ? (
            <nav className="hidden items-center gap-6 md:flex">
              <NavItem to={homeLink}>Home</NavItem>
              {isCmsAdmin ? (
                <>
                  <NavItem to={ROUTES.ADMIN_CONTENT}>Content</NavItem>
                  <NavItem to={ROUTES.ADMIN_PRODUCT_CATALOG}>Product Catalog</NavItem>
                  <NavItem to={ROUTES.ADMIN_CONTACTS}>Contacts</NavItem>
                  {sesEmailEngineEnabled && (
                    <NavItem to={ROUTES.ADMIN_SES_EMAIL_TEMPLATES}>Email Templates</NavItem>
                  )}
                </>
              ) : (
                <>
                  <ExternalNavItem href={SHOP_URL}>Shop</ExternalNavItem>
                  {!isAdmin && <NavItem to={ROUTES.SHOPIFY_CART}>Shopify Cart</NavItem>}
                  <NavItem to={isAdmin ? ROUTES.ADMIN_PATIENTS : ROUTES.PATIENTS}>Patients</NavItem>
                  {!isAdmin && <NavItem to={ROUTES.SHOP_FOR_CLIENT}>Shop for Client</NavItem>}
                  <NavLink
                    to={ROUTES.AXISMATRIX_APPROVALS}
                    className={({ isActive }) =>
                      `relative text-sm font-medium transition-colors ${isActive ? 'text-ea-blue' : 'text-ea-black hover:text-ea-blue'}`
                    }
                  >
                    AxisMatrix Approvals
                    {pendingCount > 0 && (
                      <span className="absolute -right-4 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                        {pendingCount}
                      </span>
                    )}
                  </NavLink>
                  <NavItem to={ROUTES.RESOURCES}>Education</NavItem>
                  <NavItem to={ROUTES.HELP_CENTER}>Help</NavItem>
                </>
              )}
              {isFullAdmin && (
                algorithmMgmtEnabled ? (
                  <NavItem to={ROUTES.ADMIN_ALGORITHM_MANAGEMENT}>Algorithm Management</NavItem>
                ) : (
                  <>
                    <NavItem to={ROUTES.ADMIN_HORMONES}>Edit Hormones</NavItem>
                    <NavItem to={ROUTES.ADMIN_HORMONE_LEVELS}>Edit Levels</NavItem>
                    <NavItem to={ROUTES.ADMIN_TRIGGERS}>Comment Triggers</NavItem>
                  </>
                )
              )}
              {isFullAdmin && quizEngineV2Enabled && (
                <NavItem to={ROUTES.ADMIN_AXISMATRIX_MANAGEMENT}>AxisMatrix Management</NavItem>
              )}
            </nav>
          ) : (
            <nav className="hidden items-center gap-6 md:flex">
              {SITE_NAV_LINKS.map((link) => (
                <NavItem key={link.path} to={link.path}>{link.label}</NavItem>
              ))}
            </nav>
          )}
        </div>
        <div className="flex items-center gap-4">
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => userMenuOpen.toggle()}
                className="text-sm text-ea-03 hover:text-ea-black"
                aria-label="User menu"
                aria-expanded={userMenuOpen.isOpen}
                aria-haspopup="true"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </button>
              {userMenuOpen.isOpen && (
                <div className="absolute right-0 mt-2 w-36 rounded-md border border-ea-07 bg-white py-1 shadow-lg">
                  {!isCmsAdmin && (
                    <NavLink
                      to={isAdmin ? ROUTES.ADMIN_PROFILE : ROUTES.PROFILE}
                      onClick={() => userMenuOpen.close()}
                      className="block w-full px-4 py-2 text-left text-sm text-ea-03 hover:bg-ea-11 hover:text-ea-black"
                    >
                      Profile
                    </NavLink>
                  )}
                  {isFullAdmin && (
                    <NavLink
                      to={ROUTES.ADMIN_2FA}
                      onClick={() => userMenuOpen.close()}
                      className="block w-full px-4 py-2 text-left text-sm text-ea-03 hover:bg-ea-11 hover:text-ea-black"
                    >
                      2FA Setup
                    </NavLink>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-ea-03 hover:bg-ea-11 hover:text-ea-black"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <NavLink
                to={ROUTES.LOGIN}
                className="text-sm font-medium text-ea-black hover:text-ea-blue"
              >
                Login
              </NavLink>
              <NavLink
                to={ROUTES.SIGNUP}
                className="rounded-full bg-ea-blue px-4 py-2 text-sm font-medium text-white hover:bg-ea-blue/90"
              >
                Sign Up
              </NavLink>
            </div>
          )}
        </div>
      </div>

      {/* Mobile off-canvas overlay */}
      {mobileMenuOpen.isOpen && (
        <div className="fixed inset-0 z-60 md:hidden" onClick={closeMobile}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Slide-out panel */}
          <nav
            className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-ea-07 px-6 py-4">
              <NavLink to={homeLink} onClick={closeMobile}>
                <img src="/img/EndoAxis-logo.svg" alt="EndoAxis" className="h-7" />
              </NavLink>
              <button onClick={closeMobile} aria-label="Close menu">
                <svg className="h-6 w-6 text-ea-03" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex flex-col gap-1 px-4 py-4">
              {user ? (
                <>
                  <MobileNavItem to={homeLink} onClick={closeMobile}>Home</MobileNavItem>
                  {isCmsAdmin ? (
                    <>
                      <MobileNavItem to={ROUTES.ADMIN_CONTENT} onClick={closeMobile}>Content</MobileNavItem>
                      <MobileNavItem to={ROUTES.ADMIN_PRODUCT_CATALOG} onClick={closeMobile}>Product Catalog</MobileNavItem>
                      <MobileNavItem to={ROUTES.ADMIN_CONTACTS} onClick={closeMobile}>Contacts</MobileNavItem>
                      {sesEmailEngineEnabled && (
                        <MobileNavItem to={ROUTES.ADMIN_SES_EMAIL_TEMPLATES} onClick={closeMobile}>Email Templates</MobileNavItem>
                      )}
                    </>
                  ) : (
                    <>
                      <MobileExternalNavItem href={SHOP_URL} onClick={closeMobile}>Shop</MobileExternalNavItem>
                      {!isAdmin && <MobileNavItem to={ROUTES.SHOPIFY_CART} onClick={closeMobile}>Shopify Cart</MobileNavItem>}
                      <MobileNavItem to={isAdmin ? ROUTES.ADMIN_PATIENTS : ROUTES.PATIENTS} onClick={closeMobile}>Patients</MobileNavItem>
                      {!isAdmin && (
                        <MobileNavItem to={ROUTES.SHOP_FOR_CLIENT} onClick={closeMobile}>Shop for Client</MobileNavItem>
                      )}
                      <NavLink
                        to={ROUTES.AXISMATRIX_APPROVALS}
                        onClick={closeMobile}
                        className={({ isActive }) =>
                          `flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-ea-blue/5 text-ea-blue' : 'text-ea-black hover:bg-ea-11'}`
                        }
                      >
                        AxisMatrix Approvals
                        {pendingCount > 0 && (
                          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white">
                            {pendingCount}
                          </span>
                        )}
                      </NavLink>
                      <MobileNavItem to={ROUTES.RESOURCES} onClick={closeMobile}>Education</MobileNavItem>
                      <MobileNavItem to={ROUTES.HELP_CENTER} onClick={closeMobile}>Help</MobileNavItem>
                    </>
                  )}
                  {isFullAdmin && (
                    algorithmMgmtEnabled ? (
                      <MobileNavItem to={ROUTES.ADMIN_ALGORITHM_MANAGEMENT} onClick={closeMobile}>Algorithm Management</MobileNavItem>
                    ) : (
                      <>
                        <MobileNavItem to={ROUTES.ADMIN_HORMONES} onClick={closeMobile}>Edit Hormones</MobileNavItem>
                        <MobileNavItem to={ROUTES.ADMIN_HORMONE_LEVELS} onClick={closeMobile}>Edit Levels</MobileNavItem>
                        <MobileNavItem to={ROUTES.ADMIN_TRIGGERS} onClick={closeMobile}>Comment Triggers</MobileNavItem>
                      </>
                    )
                  )}
                  {isFullAdmin && quizEngineV2Enabled && (
                    <MobileNavItem to={ROUTES.ADMIN_AXISMATRIX_MANAGEMENT} onClick={closeMobile}>AxisMatrix Management</MobileNavItem>
                  )}
                </>
              ) : (
                <>
                  <MobileNavItem to={ROUTES.LOGIN} onClick={closeMobile}>Login</MobileNavItem>
                  <MobileNavItem to={ROUTES.SIGNUP} onClick={closeMobile}>Sign Up</MobileNavItem>
                </>
              )}

              <hr className="my-2 border-ea-07" />

              {/* Site navigation links */}
              <p className="px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wider text-ea-03">
                Explore
              </p>
              {SITE_NAV_LINKS.map((link) => (
                <MobileNavItem
                  key={link.path}
                  to={link.path}
                  onClick={closeMobile}
                >
                  {link.label}
                </MobileNavItem>
              ))}

              {user && (
                <>
                  <hr className="my-2 border-ea-07" />

                  <MobileNavItem to={isAdmin ? ROUTES.ADMIN_PROFILE : ROUTES.PROFILE} onClick={closeMobile}>Profile</MobileNavItem>
                  {isAdmin && (
                    <MobileNavItem to={ROUTES.ADMIN_2FA} onClick={closeMobile}>2FA Setup</MobileNavItem>
                  )}
                  <button
                    onClick={() => { closeMobile(); handleLogout(); }}
                    className="rounded-md px-3 py-2 text-left text-sm font-medium text-ea-03 hover:bg-ea-11 hover:text-ea-black"
                  >
                    Log Out
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileNavItem({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-ea-blue/5 text-ea-blue' : 'text-ea-black hover:bg-ea-11'}`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileExternalNavItem({ href, onClick, children }: { href: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="rounded-md px-3 py-2 text-sm font-medium text-ea-black hover:bg-ea-11"
    >
      {children}
    </a>
  );
}
